import { getAccessToken, setAccessToken } from "./token-store";

const channel =
  typeof BroadcastChannel === "undefined"
    ? null
    : new BroadcastChannel("sanchezcloud.auth");
let localRefresh: Promise<string | null> | null = null;

channel?.addEventListener(
  "message",
  (event: MessageEvent<{ type: string; token?: string }>) => {
    if (event.data.type === "token") setAccessToken(event.data.token ?? null);
    if (event.data.type === "logout") setAccessToken(null);
  },
);

async function rotate(): Promise<string | null> {
  try {
    const response = await fetch("/api/v1/auth/refresh", {
      method: "POST",
      credentials: "same-origin",
    });
    if (!response.ok) {
      setAccessToken(null);
      return null;
    }
    const body = (await response.json()) as { access_token: string };
    setAccessToken(body.access_token);
    channel?.postMessage({ type: "token", token: body.access_token });
    return body.access_token;
  } catch {
    setAccessToken(null);
    return null;
  }
}

async function refreshOrReuseRotatedToken(
  rejectedToken?: string | null,
): Promise<string | null> {
  const currentToken = getAccessToken();

  // An initial application bootstrap may reuse an access token that is already
  // in memory. After a 401, however, the rejected token must never be reused.
  // A different current token means another tab or request rotated it while we
  // were waiting for the refresh lock, so that newer token is safe to reuse.
  if (rejectedToken === undefined) return currentToken ?? rotate();
  if (currentToken && currentToken !== rejectedToken) return currentToken;
  return rotate();
}

export async function refreshAccessToken(
  rejectedToken?: string | null,
): Promise<string | null> {
  if (localRefresh) return localRefresh;
  localRefresh = (async () => {
    if ("locks" in navigator) {
      return navigator.locks.request("sanchezcloud.refresh", async () =>
        refreshOrReuseRotatedToken(rejectedToken),
      );
    }
    return refreshOrReuseRotatedToken(rejectedToken);
  })().finally(() => {
    localRefresh = null;
  });
  return localRefresh;
}

export function announceLogout() {
  setAccessToken(null);
  channel?.postMessage({ type: "logout" });
}
